import { useEffect, useMemo, useState } from "react";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasStore } from "./store/canvas-store";
import { StickyObject } from "./objects/sticky-object";
import { ShapeObject } from "./objects/shape-object";
import { TextObject } from "./objects/text-object";
import { ImageObject } from "./objects/image-object";
import { VideoObject } from "./objects/video-object";
import { LinkCardObject } from "./objects/link-card-object";
import { PenStrokeObject } from "./objects/pen-stroke-object";
import { aabbIntersects, viewportInStage } from "../../lib/culling";

// オブジェクト全件を画面に描画するレイヤ。
// zOrder の昇順で並べ、後で描画されるもの（=zOrder が大きいもの）が上に重なる。
// 同 zOrder の場合は id で安定ソートする。各オブジェクトの root 要素には
// `style.zIndex = zOrder` を付与し、CSS の重なり順も zOrder と一致させる
// （オブジェクト自身の position: absolute と組み合わせて機能する）。
//
// ビューポートカリング:
// 表示中の screen 矩形をステージ座標に逆変換し、各オブジェクトの AABB と
// 交差するもののみを描画する。rotation を持つ object のはみ出し対策に
// 256px のパディングを加える（最大 30%程度の回転までは十分な余白）。
// 5,000 オブジェクト hard limit を踏まえても線形フィルタで毎フレーム十分高速。
//
// ペンストロークも通常オブジェクトとして描画し、zOrder に従って重なり順を揃える。
const CULLING_PAD_PX = 256;

export function CanvasObjectsLayer(): JSX.Element {
  const objects = useCanvasDocStore((s) => s.objects);
  const view = useCanvasStore((s) => s.view);
  // ウィンドウサイズを reactive な state として保持し、resize で再カリングする。
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window === "undefined" ? 0 : window.innerWidth,
    height: typeof window === "undefined" ? 0 : window.innerHeight,
  }));

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = (): void => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const ordered = useMemo(() => {
    return Object.values(objects).slice().sort((a, b) => {
      if (a.zOrder !== b.zOrder) return a.zOrder - b.zOrder;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  }, [objects]);

  // 選択中オブジェクトはカリング対象から外して必ず描画する。
  // CanvasSelectionOverlay が targetRef で対象 DOM を握っているため、選択中に
  // カリングでアンマウントされるとリサイズ/回転プレビューが効かなくなる。
  const selectedIds = useCanvasStore((s) => s.selectedObjectIds);

  const visible = useMemo(() => {
    if (viewportSize.width === 0 || viewportSize.height === 0) return ordered;
    const viewport = viewportInStage(
      view,
      viewportSize.width,
      viewportSize.height,
      CULLING_PAD_PX
    );
    const selectedSet = new Set(selectedIds);
    return ordered.filter(
      (obj) =>
        selectedSet.has(obj.id) ||
        aabbIntersects(viewport, { x: obj.x, y: obj.y, w: obj.w, h: obj.h })
    );
  }, [ordered, view, viewportSize, selectedIds]);

  return (
    <div data-testid="canvas-objects-layer" style={{ position: "absolute", inset: 0 }}>
      {visible.map((obj) => {
        switch (obj.type) {
          case "sticky":
            return <StickyObject key={obj.id} object={obj} />;
          case "shape":
            return <ShapeObject key={obj.id} object={obj} />;
          case "text":
            return <TextObject key={obj.id} object={obj} />;
          case "image":
            return <ImageObject key={obj.id} object={obj} />;
          case "video":
            return <VideoObject key={obj.id} object={obj} />;
          case "link-card":
            return <LinkCardObject key={obj.id} object={obj} />;
          case "pen-stroke":
            return <PenStrokeObject key={obj.id} object={obj} />;
          default:
            return null;
        }
      })}
    </div>
  );
}
